import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useGoalCreateMutation } from "web_myplann/Api/Goals/useGoalCreateMutation";
import { useGoalUpdateMutation } from "web_myplann/Api/Goals/useGoalUpdateMutation";
import { GOAL_NAME_MAX_LENGTH } from "web_myplann/Goals/constants";
import { mapGoalFormToCreateRequest } from "web_myplann/Goals/Mappers/mapGoalFormToCreateRequest";
import { mapGoalFormToUpdateRequest } from "web_myplann/Goals/Mappers/mapGoalFormToUpdateRequest";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { z } from "zod";

export const RENT_DRAWDOWN_MODES = ["to-zero", "perpetual"] as const;
export type RentDrawdownMode = (typeof RENT_DRAWDOWN_MODES)[number];

const getGoalFormSchema = (t: TranslateFunction) =>
	z.object({
		category: z.nativeEnum(GoalCategoryTypeEnum, {
			required_error: t(Resources.Form.Validations.required),
		}),
		name: z
			.string({
				required_error: t(Resources.Form.Validations.required),
			})
			.trim()
			.min(1, t(Resources.Form.Validations.required))
			.max(
				GOAL_NAME_MAX_LENGTH,
				t(Resources.Goals.Form.nameMaxLength, { max: GOAL_NAME_MAX_LENGTH }),
			),
		dueDate: z.date({
			required_error: t(Resources.Form.Validations.required),
			invalid_type_error: t(Resources.Form.Validations.required),
		}),
		totalValue: z
			.number({
				required_error: t(Resources.Form.Validations.required),
				invalid_type_error: t(Resources.Form.Validations.required),
			})
			.positive(),
		ownFunds: z.number().min(0).nullish(),
		loanAmount: z.number().min(0).nullish(),
		monthlyRent: z.number().min(0).nullish(),
		rentYears: z.number().min(0).nullish(),
		drawdownMode: z.enum(RENT_DRAWDOWN_MODES).optional(),
		statePension: z.number().min(0).nullish(),
	});

export type GoalFormModel = z.infer<ReturnType<typeof getGoalFormSchema>>;

type UseGoalFormProps = {
	goalId?: number;
	initialValues?: Partial<GoalFormModel>;
	onSuccess?: (goalId?: number) => void;
};

export const getDefaultDueDate = () => {
	const now = new Date();
	return new Date(now.getFullYear() + 5, now.getMonth(), 1);
};

export const useGoalForm = ({
	goalId,
	initialValues,
	onSuccess,
}: UseGoalFormProps) => {
	const { t } = useResource();
	const { mutate: createGoal, isPending: isCreating } = useGoalCreateMutation();
	const { mutate: updateGoal, isPending: isUpdating } = useGoalUpdateMutation();

	const form = useForm<GoalFormModel>({
		resolver: zodResolver(getGoalFormSchema(t)),
		mode: "onChange",
		defaultValues: {
			category: undefined,
			name: "",
			dueDate: getDefaultDueDate(),
			ownFunds: 0,
			loanAmount: 0,
			drawdownMode: "to-zero",
		},
	});

	const appliedInitialRef = useRef(false);
	useEffect(() => {
		if (appliedInitialRef.current || !initialValues) return;
		const defined = Object.fromEntries(
			Object.entries(initialValues).filter(([, v]) => v !== undefined),
		) as Partial<GoalFormModel>;
		if (Object.keys(defined).length === 0) return;
		appliedInitialRef.current = true;
		form.reset(
			{ ...form.getValues(), ...defined },
			{ keepDefaultValues: true },
		);
	}, [form, initialValues]);

	const submit = form.handleSubmit((data) => {
		if (goalId != null) {
			updateGoal(
				{ id: goalId, body: mapGoalFormToUpdateRequest(data) },
				{ onSuccess: () => onSuccess?.(goalId) },
			);
			return;
		}
		createGoal(mapGoalFormToCreateRequest(data), {
			onSuccess: (response) => onSuccess?.(response.id ?? undefined),
		});
	});

	return {
		...form,
		submit,
		isSubmitting: isCreating || isUpdating,
	};
};

export type UseGoalFormReturn = ReturnType<typeof useGoalForm>;
